import React, { useState } from "react";
import {
  Button,
  Divider,
  ScrollShadow,
  Image,
  Chip,
  Avatar,
  Tooltip,
  Dropdown,
  DropdownTrigger,
  DropdownMenu,
  DropdownItem,
} from "@nextui-org/react";
import { observer } from "mobx-react-lite";
import synkChainScanPageStore from "../store/store.SynkChainScanPage";
import { useIsDesktop } from "utils/useIsDesktop";

const Sidebar = observer(() => {
  const isDesktop = useIsDesktop();
  const [selectedChain, setSelectedChain] = useState<string | null>(null);

  const getMostUsedExplorers = () => {
    const mostUsed: { chain: string; explorer: string; url: string }[] = [];
    synkChainScanPageStore.chains.forEach((chain) => {
      chain.explorers.forEach((explorer) => {
        if (explorer.mostUsed) {
          mostUsed.push({
            chain: chain.name,
            explorer: explorer.name,
            url: explorer.url,
          });
        }
      });
    });
    return mostUsed;
  };

  const handleExplorerSelect = (chain: string, explorer: string) => {
    const chainObj = synkChainScanPageStore.chains.find(
      (c) => c.name === chain
    );
    if (chainObj) {
      const explorerObj = chainObj.explorers.find((e) => e.name === explorer);
      if (explorerObj) {
        synkChainScanPageStore.selectChain(chainObj);
        synkChainScanPageStore.selectExplorer(explorerObj);
      }
    }
    setSelectedChain(null);
  };

  return (
    <div
      className="p-4 pt-2.5 pl-1.5 flex flex-col h-full max-h-full gap-5"
      data-testid="sidebar-component"
    >
      <ScrollShadow className="-mr-4 h-full max-h-full py-6 pt-0 pr-2">
        {/* Chains Section */}
        <div className="grid grid-cols-2 sm:grid-cols-2 gap-3">
          {synkChainScanPageStore.chains.map((chain) => (
            <Dropdown
              classNames={{
                content: `py-1 px-1 ${
                  synkChainScanPageStore.isDarkMode
                    ? "bg-[#121212]"
                    : "bg-[hsl(240,4.76%,90.88%)]"
                } rounded-2xl`,
                base: `${
                  synkChainScanPageStore.isDarkMode
                    ? "bg-[#121212]"
                    : "bg-[hsl(240,4.76%,90.88%)]"
                } rounded-2xl border ${
                  synkChainScanPageStore.isDarkMode
                    ? "border-[#272b30]"
                    : "border-black/20"
                }`,
              }}
              backdrop="opaque"
              key={chain.name}
            >
              <DropdownTrigger>
                <Button
                  radius="sm"
                  className={`h-12 w-full backdrop-blur-dock ${
                    synkChainScanPageStore.isDarkMode
                      ? "text-white"
                      : "text-black"
                  } ${
                    synkChainScanPageStore.isDarkMode
                      ? "bg-customGray/40"
                      : "bg-[hsl(240,4.76%,95.88%)]"
                  }`}
                >
                  <div className="flex items-center justify-center gap-2">
                    <Image
                      src={chain.logo}
                      alt={chain.name}
                      width={24}
                      height={24}
                      className="object-contain"
                    />
                  </div>
                </Button>
              </DropdownTrigger>
              <DropdownMenu
                variant="faded"
                aria-label={`${chain.name} explorers`}
                classNames={{
                  base: synkChainScanPageStore.isDarkMode
                    ? "bg-[#121212]"
                    : "bg-[hsl(240,4.76%,90.88%)]",
                }}
              >
                {chain.explorers.map((explorer) => (
                  <DropdownItem
                    key={explorer.name}
                    onClick={() =>
                      handleExplorerSelect(chain.name, explorer.name)
                    }
                    className={`${
                      synkChainScanPageStore.isDarkMode
                        ? "data-[hover=true]:bg-customGray/40"
                        : "data-[hover=true]:bg-[hsl(240,4.76%,85.88%)]"
                    }`}
                  >
                    <div className="flex items-center w-full">
                      <Avatar
                        classNames={{
                          base: `bg-transparent border ${
                            synkChainScanPageStore.isDarkMode
                              ? "border-divider"
                              : "border-black/20 bg-white"
                          }`,
                          name: `text-[0.6rem] font-semibold ${
                            synkChainScanPageStore.isDarkMode
                              ? "text-default-500"
                              : "text-black/60"
                          }`,
                        }}
                        getInitials={(name) => {
                          const first = name[0] || "";
                          const last = name[name.length - 1] || "";
                          return (first + last).toUpperCase();
                        }}
                        name={explorer.name}
                        radius="md"
                        size="sm"
                      />
                      <span
                        className={`ml-2 truncate flex-1 text-left ${
                          synkChainScanPageStore.isDarkMode
                            ? "text-default-500"
                            : "text-black/90"
                        }`}
                      >
                        {explorer.name}
                      </span>
                    </div>
                  </DropdownItem>
                ))}
              </DropdownMenu>
            </Dropdown>
          ))}
        </div>

        <Divider className="my-4" />

        <div className="flex flex-col w-full gap-2 px-2">
          <h3
            className={`text-sm font-medium mb-2 ${
              synkChainScanPageStore.isDarkMode
                ? "text-default-500"
                : "text-black/60"
            }`}
          >
            Most Used Explorers
          </h3>
          {getMostUsedExplorers().map((item) => (
            <Button
              key={`${item.chain}-${item.explorer}`}
              variant="light"
              className="flex justify-start w-full"
              onClick={() => handleExplorerSelect(item.chain, item.explorer)}
            >
              <div className="flex items-center w-full">
                <Avatar
                  classNames={{
                    base: `bg-transparent border ${
                      synkChainScanPageStore.isDarkMode
                        ? "border-divider"
                        : "border-black/20 bg-white"
                    }`,
                    name: `text-[0.6rem] font-semibold ${
                      synkChainScanPageStore.isDarkMode
                        ? "text-default-500"
                        : "text-black/60"
                    }`,
                  }}
                  getInitials={(name) => {
                    const first = name[0] || "";
                    const last = name[name.length - 1] || "";
                    return (first + last).toUpperCase();
                  }}
                  name={item.explorer}
                  radius="md"
                  size="sm"
                />
                <span
                  className={`ml-2 truncate flex-1 text-left ${
                    synkChainScanPageStore.isDarkMode
                      ? "text-default-500"
                      : "text-black/90"
                  }`}
                >
                  {item.explorer}
                </span>
              </div>
            </Button>
          ))}
        </div>
      </ScrollShadow>
    </div>
  );
});

export default Sidebar;
